import React, { useState } from 'react';
import { CreditCard, Shield, AlertTriangle, ChevronRight } from 'lucide-react';
import type { SavedPaymentMethod } from '@/types';

interface SavedPaymentMethodConfirmationProps {
  paymentMethod: SavedPaymentMethod;
  estimatedCost: number;
  maxPossibleCost: number;
  onConfirm: () => void;
  onUseDifferentMethod: () => void;
  isProcessing: boolean;
  mode?: 'batch' | 'search'; // Determines messaging context
}

const CARD_BRAND_COLORS = {
  visa: 'bg-blue-500',
  mastercard: 'bg-orange-500',
  amex: 'bg-green-500',
  discover: 'bg-purple-500',
  default: 'bg-slate-500',
};

export const SavedPaymentMethodConfirmation: React.FC<SavedPaymentMethodConfirmationProps> = ({
  paymentMethod,
  estimatedCost,
  maxPossibleCost,
  onConfirm,
  onUseDifferentMethod,
  isProcessing,
  mode = 'batch', // Default to batch for backward compatibility
}) => {
  const [agreeToAuthorize, setAgreeToAuthorize] = useState(false);
  const [fcraAcknowledgment, setFcraAcknowledgment] = useState(false);
  const brandColor = CARD_BRAND_COLORS[paymentMethod.brand.toLowerCase() as keyof typeof CARD_BRAND_COLORS] || CARD_BRAND_COLORS.default;

  // Determine if this is a fixed-price transaction (search) or variable (batch)
  const isFixedPrice = mode === 'search' || estimatedCost === maxPossibleCost;
  const isSearch = mode === 'search';

  const handleConfirm = () => {
    if (!agreeToAuthorize || !fcraAcknowledgment) {
      return;
    }
    onConfirm();
  };

  return (
    <div className="ca-card  p-4">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Payment Authorization</h2>

      {/* Saved Payment Method Display */}
      <div className="mb-6">
        <p className="text-sm text-slate-600 mb-3">
          We'll use your saved payment method for this {isSearch ? 'search' : 'batch'}:
        </p>
        <div className="border border-slate-200 rounded-lg p-4 flex items-center gap-4 bg-slate-50">
          <div className={`w-14 h-10 ${brandColor} rounded flex items-center justify-center flex-shrink-0`}>
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 capitalize">
              {paymentMethod.brand} •••• {paymentMethod.last4}
            </p>
            <p className="text-xs text-slate-600">
              Expires {paymentMethod.expiryMonth.toString().padStart(2, '0')}/{paymentMethod.expiryYear}
            </p>
          </div>
        </div>
      </div>

      {/* Authorization Terms - Only show for batch processing */}
      {!isSearch && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-900">
              {isFixedPrice ? (
                <>
                  <p className="font-semibold mb-1">Payment Authorization</p>
                  <p className="mb-2">
                    We'll authorize <span className="font-semibold">${estimatedCost.toFixed(2)}</span> on your card for this batch.
                  </p>
                  <p className="text-xs text-blue-800">
                    You'll be charged the full amount once processing completes successfully.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold mb-1">Pre-Authorization Hold</p>
                  <p className="mb-2">
                    We'll authorize <span className="font-semibold">${maxPossibleCost.toFixed(2)}</span> on your card while processing runs.
                    Once complete, we'll only capture <span className="font-semibold">${estimatedCost.toFixed(2)}</span> (estimated)
                    based on successfully processed rows, and release the remaining hold immediately.
                  </p>
                  <p className="text-xs text-blue-800">
                    You'll only be charged for rows that successfully complete processing.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Authorization Checkboxes */}
      <div className="space-y-4 mb-6">
        <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={agreeToAuthorize}
            onChange={(e) => setAgreeToAuthorize(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
          />
          <span>
            {isSearch ? (
              <>
                I authorize CollectABILITY to charge{' '}
                <span className="font-semibold">${estimatedCost.toFixed(2)}</span> to my {paymentMethod.brand} ending in {paymentMethod.last4}{' '}
                for this search. If no identity match is found, the charge will be cancelled.
              </>
            ) : isFixedPrice ? (
              <>
                I authorize CollectABILITY to charge{' '}
                <span className="font-semibold">${estimatedCost.toFixed(2)}</span> to my {paymentMethod.brand} ending in {paymentMethod.last4}{' '}
                for processing this batch.
              </>
            ) : (
              <>
                I authorize CollectABILITY to pre-authorize up to{' '}
                <span className="font-semibold">${maxPossibleCost.toFixed(2)}</span> on my {paymentMethod.brand} ending in {paymentMethod.last4},
                and to capture the actual amount based on successfully processed records.
              </>
            )}
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={fcraAcknowledgment}
            onChange={(e) => setFcraAcknowledgment(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
          />
          <span>
            I acknowledge that CollectABILITY is <span className="font-semibold">not a Consumer Reporting Agency (CRA)</span> as defined by the Fair Credit Reporting Act (FCRA), and that I will not use this service for employment, credit, tenant screening, or any FCRA-related purpose.
          </span>
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!agreeToAuthorize || !fcraAcknowledgment || isProcessing}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed hover:bg-blue-700"
        >
          {isProcessing ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              {isSearch ? 'Processing...' : 'Authorizing...'}
            </>
          ) : (
            <>
              <Shield className="w-4 h-4" />
              {isSearch ? 'Confirm & Search' : 'Authorize & Start Processing'}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onUseDifferentMethod}
          disabled={isProcessing}
          className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 px-4 py-2 rounded-lg font-medium border border-slate-300 transition-colors hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Use Different Payment Method
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Error Display */}
      {(!agreeToAuthorize || !fcraAcknowledgment) && (
        <div className="mt-4 text-sm text-slate-500 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Please check both boxes to continue
        </div>
      )}
    </div>
  );
};


